import { Component } from '@theme/component';
import { fetchConfig, preloadImage } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';
import { CartLinesUpdateEvent, CartErrorEvent, ProductSelectEvent, StandardEvents } from '@shopify/events';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

// If Shopify does not settle cart mutation + reconciliation, fail safe and require a cart reload.
const CART_OPERATION_TIMEOUT_MS = 20000;

/** One storefront tab must not issue overlapping cart mutations whose re-reads can settle out of order. */
let activeCartMutation = null;
let cartStateUncertain = false;

// Pointer CSS is not enough for keyboard/synthetic activation, and a quick-add modal can insert
// new purchase controls after the initial lock snapshot. Root state is the authoritative guard.
document.addEventListener(
  'click',
  (event) => {
    if (
      !document.documentElement.matches('[data-pp-cart-mutating], [data-pp-cart-state-uncertain]') ||
      !(event.target instanceof Element) ||
      !event.target.closest('.add-to-cart-button, .accelerated-checkout-block')
    ) {
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
  },
  true
);

function lockAllAcceleratedCheckout() {
  for (const container of document.querySelectorAll('.accelerated-checkout-block')) {
    if (!(container instanceof HTMLElement) || container.hasAttribute('data-pp-global-cart-updating')) continue;
    container.dataset.ppGlobalWasInert = container.inert ? 'true' : 'false';
    container.setAttribute('data-pp-global-cart-updating', '');
    container.setAttribute('inert', '');
    container.setAttribute('aria-hidden', 'true');
    container.setAttribute('aria-busy', 'true');
  }
}

function unlockAllAcceleratedCheckout() {
  for (const container of document.querySelectorAll('.accelerated-checkout-block[data-pp-global-cart-updating]')) {
    if (!(container instanceof HTMLElement)) continue;
    const wasInert = container.dataset.ppGlobalWasInert === 'true';
    container.removeAttribute('data-pp-global-cart-updating');
    delete container.dataset.ppGlobalWasInert;
    const remainsBlocked =
      container.hidden ||
      container.matches(
        '[data-pp-variant-updating], [data-pp-variant-blocked], [data-pp-cart-updating], [data-pp-cart-blocked]'
      );
    if (!wasInert && !remainsBlocked) {
      container.removeAttribute('inert');
      container.removeAttribute('aria-hidden');
    }
    if (!container.matches('[data-pp-variant-updating], [data-pp-cart-updating]')) {
      container.removeAttribute('aria-busy');
    }
  }
}

function blockAllAcceleratedCheckout() {
  for (const container of document.querySelectorAll('.accelerated-checkout-block')) {
    if (!(container instanceof HTMLElement)) continue;
    container.removeAttribute('data-pp-global-cart-updating');
    container.setAttribute('data-pp-global-cart-blocked', '');
    container.setAttribute('inert', '');
    container.setAttribute('aria-hidden', 'true');
    container.removeAttribute('aria-busy');
    delete container.dataset.ppGlobalWasInert;
  }
}

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /** Runs only from the correlated CartLinesUpdate success handler. */
  playConfirmedFlyToCart() {
    if (this.refs.addToCartButton.dataset.puppet === 'true') return;
    if (this.dataset.addToCartAnimation !== 'true' || this.closest('.quick-add-modal')) return;
    this.#animateFlyToCart();
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} addToCartErrorMessage - The dedicated add-to-cart error message node.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** Prevents repeated submit/keyboard activation from creating duplicate cart mutations. */
  #submitInProgress = false;

  /** A timed-out mutation has an unknown server outcome; only a reload may safely unlock buying. */
  #purchaseStateUncertain = false;

  /** @type {number} */
  #variantChangeGeneration = 0;

  /** Last product URL backed by a successful section response. */
  #confirmedUrl = window.location.href;

  connectedCallback() {
    super.connectedCallback();

    // Shopify can temporarily detach and reinsert this component while morphing/hydrating a
    // section. An AbortSignal cannot be reused after disconnectedCallback aborts it.
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  #getVariantIdInput() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'))?.value;
  }

  async #refreshCart(signal) {
    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
      signal,
    }).then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    });
  }

  /** @param {HTMLElement | undefined} container */
  #syncAcceleratedCheckoutInteractivity(container) {
    if (!container) return;
    const isUpdating =
      container.hasAttribute('data-pp-variant-updating') || container.hasAttribute('data-pp-cart-updating');
    const isBlocked =
      container.hidden ||
      isUpdating ||
      container.hasAttribute('data-pp-variant-blocked') ||
      container.hasAttribute('data-pp-cart-blocked');

    container.toggleAttribute('inert', isBlocked);
    if (isBlocked) container.setAttribute('aria-hidden', 'true');
    else container.removeAttribute('aria-hidden');
    if (isUpdating) container.setAttribute('aria-busy', 'true');
    else container.removeAttribute('aria-busy');
  }

  /** @param {HTMLElement | undefined} container @param {boolean} pending */
  #setAcceleratedCheckoutCartPending(container, pending) {
    if (!container) return;
    container.toggleAttribute('data-pp-cart-updating', pending);
    this.#syncAcceleratedCheckoutInteractivity(container);
  }

  /** @param {HTMLElement | undefined} container */
  #blockAcceleratedCheckoutAfterCartTimeout(container) {
    if (!container) return;
    container.removeAttribute('data-pp-cart-updating');
    container.setAttribute('data-pp-cart-blocked', '');
    this.#syncAcceleratedCheckoutInteractivity(container);
  }

  /** @param {HTMLElement | undefined} container */
  #setAcceleratedCheckoutVariantPending(container) {
    if (!container) return;
    container.removeAttribute('data-pp-variant-blocked');
    container.setAttribute('data-pp-variant-updating', '');
    this.#syncAcceleratedCheckoutInteractivity(container);
  }

  /** @param {HTMLElement | undefined} container @param {boolean} available */
  #setAcceleratedCheckoutVariantResolved(container, available) {
    if (!container) return;
    container.removeAttribute('data-pp-variant-updating');
    container.removeAttribute('data-pp-variant-blocked');
    container.toggleAttribute('hidden', !available);
    this.#syncAcceleratedCheckoutInteractivity(container);
  }

  /** @param {HTMLElement | undefined} container */
  #setAcceleratedCheckoutVariantFailed(container) {
    if (!container) return;
    container.removeAttribute('data-pp-variant-updating');
    container.setAttribute('data-pp-variant-blocked', '');
    this.#syncAcceleratedCheckoutInteractivity(container);
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   */
  #updateCartQuantity(cart) {
    const variantIdInput = this.#getVariantIdInput();
    if (!variantIdInput) return;

    const cartQty = this.#cartQuantityForVariant(cart, variantIdInput);
    this.dataset.cartQuantity = cartQty.toString();

    // Use public API to update quantity selector
    const quantitySelector =
      /** @type {import('@theme/component-cart-quantity-selector').CartQuantitySelectorComponent | null} */ (
        this.querySelector('quantity-selector-component')
      );

    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);
  }

  /** @param {Cart} cart @param {string | number} variantId */
  #cartQuantityForVariant(cart, variantId) {
    return cart.items
      .filter(
        /** @param {any} item */
        (item) => item.variant_id.toString() === variantId.toString()
      )
      .reduce(
        /** @param {number} total @param {any} item */
        (total, item) => total + Number(item.quantity || 0),
        0
      );
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = async (event) => {
    if (!this.#getVariantIdInput()) return;

    event.promise
      ?.then(({ detail }) => {
        // Skip if this event came from this component
        if (event.target === this) return;

        if (detail?.items) {
          this.#updateCartQuantity(/** @type {Cart} */ ({ items: detail.items }));
        } else {
          this.#refreshCart().then((cart) => this.#updateCartQuantity(cart));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-form] Event promise rejected:', error);
      });
  };

  /** @param {Event} event */
  async handleSubmit(event) {
    event.preventDefault();

    // Fail closed while the server resolves a variant. Queuing eager clicks creates ambiguous
    // purchase intent and can multiply cart mutations when a shopper double-clicks.
    if (this.#variantChangeInProgress || this.#submitInProgress || activeCartMutation || cartStateUncertain) return;

    const picker = this.#getVariantPicker();
    const acceleratedCheckout = this.refs.acceleratedCheckoutButtonContainer;
    activeCartMutation = this;
    document.documentElement.setAttribute('data-pp-cart-mutating', '');
    lockAllAcceleratedCheckout();
    this.#submitInProgress = true;
    picker?.setPurchasePending?.(true);
    this.#setAcceleratedCheckoutCartPending(acceleratedCheckout, true);
    try {
      await this.#processAddToCart(event);
    } finally {
      this.#submitInProgress = false;
      if (!this.#purchaseStateUncertain) {
        picker?.setPurchasePending?.(false);
        this.#setAcceleratedCheckoutCartPending(acceleratedCheckout, false);
      }
      if (activeCartMutation === this) activeCartMutation = null;
      if (!cartStateUncertain) {
        document.documentElement.removeAttribute('data-pp-cart-mutating');
        unlockAllAcceleratedCheckout();
      }
    }
  }

  /**
   * @param {Event} [event]
   * @returns {Promise<void> | undefined}
   */
  async #processAddToCart(event) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    const anyButtonDisabled = Array.from(allAddToCartContainers).some(
      (container) => container.refs.addToCartButton?.disabled
    );
    if (anyButtonDisabled) return;

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          this.#showAddToCartError(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);
    const requestedQuantity = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;
    const itemCount = Number.isFinite(requestedQuantity) && requestedQuantity > 0 ? requestedQuantity : 1;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();
    const cartController = new AbortController();
    const cartTimeout = window.setTimeout(() => {
      cartController.abort(new DOMException('Cart operation timed out', 'TimeoutError'));
    }, CART_OPERATION_TIMEOUT_MS);
    let mutationAttempted = false;

    // Dispatch before the first await. Sticky/quick-add correlation expects to capture this event
    // synchronously from the click that delegated to this form.
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [
          {
            merchandiseId: /** @type {string} */ (formData.get('id')),
            quantity: itemCount,
          },
        ],
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const fetchCfg = fetchConfig('javascript', { body: formData });
      // From this point onward the browser may have sent bytes. Any exception must reconcile or
      // fail closed because the server outcome is no longer knowable from the client alone.
      mutationAttempted = true;
      const fetchResponse = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
        signal: cartController.signal,
      });
      const response = await fetchResponse.json();

      if (!fetchResponse.ok || response?.status) {
        const ajaxCart = await this.#refreshCart(cartController.signal);
        const customerError =
          response?.description || response?.message || this.dataset.addToCartError || 'Add to cart failed';

        this.dispatchEvent(
          new CartErrorEvent({
            error: customerError,
            code: 'INVALID',
            detail: {
              description: response?.description,
              errors: response?.errors,
            },
          })
        );

        this.#updateCartQuantity(ajaxCart);
        if (addToCartTextError) {
          this.#showAddToCartError(customerError);
          this.#timeout = setTimeout(() => this.#clearAddToCartError(), ERROR_MESSAGE_DISPLAY_DURATION);
        }

        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: {
            didError: true,
            message: customerError,
            items: ajaxCart.items,
            source: 'product-form-component',
            sourceId: this.id.toString(),
            itemCount,
            productId: this.dataset.productId,
          },
        });
        return;
      }

      const id = formData.get('id');
      if (!id) throw new Error('Form ID is required');

      const ajaxCart = await this.#refreshCart(cartController.signal);
      if (this.#getVariantIdInput()) this.#updateCartQuantity(ajaxCart);
      this.#clearAddToCartError();

      // Announce success only after both the mutation and authoritative cart re-read succeeded.
      const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
      if (anyAddToCartButton) {
        const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
        this.#setLiveRegionText(addedTextElement?.textContent?.trim() || Theme.translations.added);
        setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
      }

      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'product-form-component',
          sourceId: this.id.toString(),
          itemCount,
          productId: this.dataset.productId,
          sections: response.sections,
          didError: false,
        },
      });
    } catch (error) {
      const rejection = cartController.signal.aborted ? cartController.signal.reason ?? error : error;
      if (!mutationAttempted) {
        console.error('[product-form] Cart request could not be prepared:', rejection);
        const eventRejection = Object.assign(new Error('Cart request could not be prepared'), {
          name: 'CartPreparationError',
        });
        deferredEventPromise.reject(eventRejection);
        const customerMessage = this.dataset.addToCartError || 'Add to cart failed';
        this.#showAddToCartError(customerMessage);
        this.#timeout = setTimeout(() => this.#clearAddToCartError(), ERROR_MESSAGE_DISPLAY_DURATION);
        this.dispatchEvent(
          new CartErrorEvent({
            error: customerMessage,
            code: 'SERVICE_UNAVAILABLE',
          })
        );
        return;
      }

      console.error('[product-form] Cart operation failed:', rejection);
      // A Shopify error response can still represent a partial inventory add. Only a successful
      // authoritative cart re-read proves the final state, so every exception here is uncertain.
      const eventRejection = Object.assign(new Error('Cart state could not be confirmed'), {
        name: 'CartStateUncertainError',
      });
      deferredEventPromise.reject(eventRejection);

      const customerMessage =
        this.dataset.cartTimeoutError ||
        "We couldn't confirm your cart update. Reload the page and check your cart before trying again.";

      this.#purchaseStateUncertain = true;
      cartStateUncertain = true;
      document.documentElement.setAttribute('data-pp-cart-state-uncertain', '');
      blockAllAcceleratedCheckout();
      for (const container of allAddToCartContainers) container.disable();
      this.#blockAcceleratedCheckoutAfterCartTimeout(this.refs.acceleratedCheckoutButtonContainer);

      this.#showAddToCartError(customerMessage);

      this.dispatchEvent(
        new CartErrorEvent({
          error: customerMessage,
          code: 'CART_STATE_UNCERTAIN',
        })
      );
    } finally {
      clearTimeout(cartTimeout);
      if (event) cartPerformance.measureFromEvent('add:user-action', event);
    }
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  #clearAddToCartError() {
    const { addToCartTextError } = this.refs;
    if (!addToCartTextError) return;

    addToCartTextError.classList.add('hidden');
    const messageElement =
      this.refs.addToCartErrorMessage ?? addToCartTextError.querySelector('.product-form-text__error-message');
    if (messageElement) messageElement.textContent = '';
    this.#clearLiveRegionText();
  }

  /** @param {string} message */
  #showAddToCartError(message) {
    const { addToCartTextError } = this.refs;
    if (!addToCartTextError) return;

    let messageElement =
      this.refs.addToCartErrorMessage ?? addToCartTextError.querySelector('.product-form-text__error-message');
    if (!messageElement) {
      messageElement = document.createElement('span');
      messageElement.className = 'product-form-text__error-message';
      messageElement.setAttribute('ref', 'addToCartErrorMessage');
      addToCartTextError.appendChild(messageElement);
    }

    messageElement.textContent = message;
    addToCartTextError.classList.remove('hidden');
    this.#setLiveRegionText(message);
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {ProductSelectEvent} event
   */
  #onProductSelect = async (event) => {
    // Skip events from product-cards when this form is at the section level
    const sourceCard = /** @type {Element | null} */ (event.target)?.closest('product-card');
    if (sourceCard && !sourceCard.contains(this)) return;
    const eventProductId = event.product?.id ? String(event.product.id).split('/').pop() : undefined;
    if (eventProductId && this.dataset.productId && eventProductId !== String(this.dataset.productId)) return;

    // Track generation to prevent a stale (aborted) call from clearing the flag
    // while a newer variant selection is still pending.
    const generation = ++this.#variantChangeGeneration;
    this.#variantChangeInProgress = true;
    if (this.#timeout) clearTimeout(this.#timeout);
    this.#clearAddToCartError();
    // Every purchase target stays empty/inert until Shopify confirms the selection.
    const { variantId, addToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    variantId.value = '';
    addToCartButtonContainer?.disable();
    this.#setAcceleratedCheckoutVariantPending(acceleratedCheckoutButtonContainer);

    try {
      const { detail } = await event.promise;
      if (!detail?.html) throw new Error('Variant section response missing HTML');

      const { html, newProduct, productId, resource } = detail;

      // Update product context if new product loaded (combined listing)
      if (newProduct) {
        this.dataset.productId = String(newProduct.id).split('/').pop() || '';
      } else if (productId && productId !== this.dataset.productId) {
        throw new Error('Variant response product mismatch');
      }

      variantId.value = resource?.id ?? '';
      const nextProductForm = html.querySelector('product-form-component');
      this.dataset.cartQuantity = nextProductForm?.getAttribute('data-cart-quantity') || '0';
      this.#confirmedUrl = window.location.href;

      const currentAddToCartButtonContainer = addToCartButtonContainer;
      const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

      // Update state and text for add-to-cart button
      if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

      // Update the button state
      if (resource == null || resource.available == false) {
        currentAddToCartButtonContainer.disable();
      } else {
        currentAddToCartButtonContainer.enable();
      }

      const newAddToCartButton = html.querySelector('product-form-component [ref="addToCartButton"]');
      if (newAddToCartButton && currentAddToCartButton) {
        morph(currentAddToCartButton, newAddToCartButton);
      }

      this.#setAcceleratedCheckoutVariantResolved(
        acceleratedCheckoutButtonContainer,
        resource != null && resource.available != false
      );

      // Set the data attribute for the product variant media if it exists
      if (resource) {
        const productVariantMedia = resource.featured_media?.preview_image?.src;
        if (productVariantMedia) {
          this.refs.addToCartButtonContainer?.setAttribute(
            'data-product-variant-media',
            productVariantMedia + '&width=100'
          );
        }
      }

      // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
      const {
        quantityRules,
        pricePerItem,
        quantitySelector,
        productFormButtons,
        quantityLabel,
        quantitySelectorWrapper,
      } = this.refs;

      // Update quantity selector's min/max/step attributes and cart quantity for the new variant
      const newQuantityInput = /** @type {HTMLInputElement | null} */ (
        html.querySelector('quantity-selector-component input[ref="quantityInput"]')
      );

      if (quantitySelector?.updateConstraints && newQuantityInput) {
        quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
        // Keep data-quantity-default attribute in sync with new variant's minimum quantity
        this.dataset.quantityDefault = newQuantityInput.min || '1';
      }

      const newQuantityRules = html.querySelector('.quantity-rules');
      const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

      const newPricePerItem = html.querySelector('price-per-item');
      const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

      if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
        // Store quantity value before morphing entire container
        const currentQuantityValue = quantitySelector.getValue?.();

        const newProductFormButtons = html.querySelector('.product-form-buttons');

        if (productFormButtons && newProductFormButtons) {
          morph(productFormButtons, newProductFormButtons);

          // Get the NEW quantity selector after morphing and update its constraints
          const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
            html.querySelector('quantity-selector-component input[ref="quantityInput"]')
          );

          if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
            // Temporarily set the old value so updateConstraints can snap it properly
            this.refs.quantitySelector.setValue(currentQuantityValue);
            // updateConstraints will snap to valid increment if needed
            this.refs.quantitySelector.updateConstraints(
              newQuantityInputElement.min,
              newQuantityInputElement.max || null,
              newQuantityInputElement.step
            );
            // Keep data-quantity-default attribute in sync with new variant's minimum quantity
            this.dataset.quantityDefault = newQuantityInputElement.min || '1';
          }
        }
      } else {
        // Update elements individually when layout isn't changing
        /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
        const morphTargets = [
          ['.quantity-label', quantityLabel, quantitySelector],
          ['.quantity-rules', quantityRules, this.refs.productFormButtons],
          ['price-per-item', pricePerItem, quantitySelectorWrapper],
        ];

        for (const [selector, currentElement, fallback] of morphTargets) {
          this.#morphOrUpdateElement(currentElement, html.querySelector(selector), fallback);
        }
      }

      // Morph volume pricing if it exists
      const currentVolumePricing = this.refs.volumePricing;
      const newVolumePricing = html.querySelector('volume-pricing');
      this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

      const hasB2BFeatures =
        quantityRules ||
        newQuantityRules ||
        pricePerItem ||
        newPricePerItem ||
        currentVolumePricing ||
        newVolumePricing;

      if (!hasB2BFeatures) return;

      // Fetch and update cart quantity for the new variant
      this.#refreshCart().then((cart) => this.#updateCartQuantity(cart));
    } catch (error) {
      // Rapid A→B selections intentionally abort A's fetch. The newer generation owns the UI, so
      // the old listener resolves quietly instead of becoming an unhandled listener promise.
      if (error?.name === 'AbortError' || generation !== this.#variantChangeGeneration) return;

      const picker = this.#getVariantPicker();
      picker?.clearFailedSelection?.(event.detail?.optionValueId);
      variantId.value = '';
      addToCartButtonContainer?.disable();
      this.#setAcceleratedCheckoutVariantFailed(acceleratedCheckoutButtonContainer);

      if (this.#confirmedUrl && window.location.href !== this.#confirmedUrl) {
        history.replaceState({}, '', this.#confirmedUrl);
      }

      const customerMessage =
        this.dataset.variantUpdateError || "We couldn't update your selection. Please choose it again.";
      this.#showAddToCartError(customerMessage);
      this.#timeout = setTimeout(() => this.#clearAddToCartError(), ERROR_MESSAGE_DISPLAY_DURATION);
      this.dispatchEvent(
        new CartErrorEvent({
          error: customerMessage,
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      // Only clear the flag if no newer variant selection has started
      if (generation === this.#variantChangeGeneration) {
        this.#variantChangeInProgress = false;
      }
    }
  };

  /**
   * @returns {import('@theme/variant-picker').default | null}
   */
  #getVariantPicker() {
    const container = this.closest('product-card') ?? this.closest('dialog') ?? this.closest('.shopify-section');
    const pickers = /** @type {import('@theme/variant-picker').default[]} */ (
      Array.from(container?.querySelectorAll('variant-picker, swatches-variant-picker-component') ?? [])
    );
    const productId = this.dataset.productId;

    if (productId) {
      const matchingPicker = pickers.find((picker) => picker.dataset.productId === productId);
      if (matchingPicker) return matchingPicker;
    }

    return pickers.length === 1 ? pickers[0] ?? null : null;
  }

}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
